import { useMemo, useState } from 'react';

import { getExtensions } from 'approot/shared/richtext/richtext.utils';
import { useActivityBuilderTheme } from 'approot/activity-builder/theme/activity-builder-theme';
import {
  getEditableCellsRowHeight,
  getTableHeaderAndBodyRows,
  isCellEmpty,
} from './table-view.utils';
import { getRichtextAnswer } from '../shared/logic/get-richtext-answer';
import { getRichtextValue } from '../shared/logic/get-richtext-value';
import type { AnswerFragment } from '../../present/activity-answers.graphql.generated';
import { InquisitiveActivitiesAnswerType } from 'global/graphql/scalars.type';
import type { GetActivitiesSectionsQuery } from '../../lesson-builder.graphql.generated';
import { RICHTEXT_SETTINGS, ViewProps } from './table.constants';
import { ColGroupsView } from './table-common';
import * as Styled from './table.style';
import type { Pluck } from 'lib/gql/pluck';

const TableReadonlyAnswers = ({
  properties,
  answers,
  isSuggestedAnswer,
  width,
}: ViewProps & {
  answers:
    | {
        uuid: string;
        componentId: string;
        activityId: string;
        answerType: InquisitiveActivitiesAnswerType | null;
        answer:
          | Pluck<AnswerFragment, 'answer'>
          | Pluck<
              Extract<
                Pluck<
                  GetActivitiesSectionsQuery,
                  'getActivitiesSections.components'
                >,
                { __typename: 'ActivitiesTableComponent' }
              >,
              'suggestedAnswerProperties.suggestedAnswers.answer'
            >
          | null;
        answerReferences: Pluck<AnswerFragment, 'answerReferences'> | null;
      }[]
    | null;
  isSuggestedAnswer?: boolean;
  width?: number;
}) => {
  const [tableWrapperRef, setTableWrapperRef] = useState<HTMLDivElement | null>(
    null
  );
  const { theme } = useActivityBuilderTheme();

  const extensions = getExtensions(RICHTEXT_SETTINGS);
  const table = properties.table;

  const { tableHeaderRow, tableBodyRows } = useMemo(
    () => getTableHeaderAndBodyRows(table),
    [table]
  );

  const headerTextAreaRowHeight = useMemo(() => {
    return getEditableCellsRowHeight(tableHeaderRow?.cells || []);
  }, [tableHeaderRow]);

  return (
    <div
      ref={setTableWrapperRef}
      style={width ? { maxWidth: `${width}px`, margin: 'auto' } : undefined}
    >
      <Styled.Table cellSpacing="0" key={`rows-${table.rows.length}`}>
        <ColGroupsView
          columnWidths={table.columnWidths}
          containerElement={tableWrapperRef}
        />
        {tableHeaderRow && (
          <thead>
            <Styled.TableRow>
              {tableHeaderRow?.cells.map((header, index) => {
                const cellKey = `row-1-column-${index}`;
                const answer =
                  answers?.find(
                    ans =>
                      ans.answerReferences?.row === 0 &&
                      ans.answerReferences.col === index
                  ) || null;

                return (
                  <Styled.TableCell $highlight key={cellKey}>
                    <Styled.TableCellView
                      $highlight
                      $isAnswer={!!(isSuggestedAnswer && answer)}
                      $baseFontSize={theme.font.medium.size}
                      $baseLineHeight={theme.font.small.lineHeight}
                      $rows={headerTextAreaRowHeight}
                      dangerouslySetInnerHTML={{
                        __html: !isCellEmpty(header.value)
                          ? getRichtextValue(header.value, extensions)
                          : getRichtextValue(
                              getRichtextAnswer(answer),
                              extensions
                            ),
                      }}
                    />
                  </Styled.TableCell>
                );
              })}
            </Styled.TableRow>
          </thead>
        )}
        <tbody>
          {tableBodyRows.map((row, tableBodyIndex) => {
            const rowIndex = table.showRowHeaders
              ? tableBodyIndex + 1
              : tableBodyIndex;
            const editorRows = getEditableCellsRowHeight(row.cells);
            return (
              <Styled.TableRow key={`row-${rowIndex}`}>
                {row.cells.map((cell, cellIndex) => {
                  const cellKey = `row-${rowIndex}-cell-${cellIndex}-header`;
                  const answer =
                    answers?.find(
                      ans =>
                        ans.answerReferences?.row === rowIndex &&
                        ans.answerReferences.col === cellIndex
                    ) || null;

                  return (
                    <Styled.TableCell
                      key={cellKey}
                      $highlight={table.showColumnHeaders && cellIndex === 0}
                    >
                      <Styled.TableCellView
                        $highlight={table.showColumnHeaders && cellIndex === 0}
                        $baseFontSize={theme.font.medium.size}
                        $baseLineHeight={theme.font.small.lineHeight}
                        $isAnswer={!!(isSuggestedAnswer && answer)}
                        $rows={editorRows}
                        dangerouslySetInnerHTML={{
                          __html: !isCellEmpty(cell.value)
                            ? getRichtextValue(cell.value, extensions)
                            : getRichtextValue(
                                getRichtextAnswer(answer),
                                extensions
                              ),
                        }}
                      />
                    </Styled.TableCell>
                  );
                })}
              </Styled.TableRow>
            );
          })}
        </tbody>
      </Styled.Table>
    </div>
  );
};

export default TableReadonlyAnswers;
